import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Formik, Form } from 'formik';
import reduce from 'lodash/reduce';
import keyBy from 'lodash/keyBy';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import forEach from 'lodash/forEach';
import { Intent, NonIdealState, Tooltip, Position } from '@blueprintjs/core';

import { fetchQuestionnaireInstance } from 'actions/questionnaire';
import { fetchPrescreenInstance } from 'actions/prescreen';
import { fetchVendorRequest } from 'actions/vendor-request';
import { fetchUploadedFiles } from 'actions/utils';
import { saveQuestionnaire } from 'api/questionnaire';
import { uploadFile } from 'api/utils';
import { acceptPrescreenAnswers } from 'api/vendor-request';

import QuestionTypeSection from 'containers/Questionnaire/QuestionTypeSection';
import Loading from 'components/loading';
import {
  Container,
  PlainButton,
  PaddingWrapper,
  DrawerHeader,
} from 'components/styled-components';
import { ReviewPrescreen } from 'components/forms/questionnaire/review-prescreen';
import AddAttachmentsSection from 'components/forms/questionnaire/add-attachments';
import UploadedAttachments from 'components/forms/questionnaire/uploaded-attachments';
import { AppToaster } from 'components/AppToaster';
import { fileTypeFilter } from 'utils/functions';
import permissions from 'utils/permissions';
import hasPermission from 'utils/hasPermission';

const groupQuestionsByType = x =>
  reduce(
    groupBy(x, 'questionType.typeName'),
    (result, typeSection, key) => {
      const categoryGroup = groupQuestionsByCategory(typeSection);
      const group = {
        key,
        typeID: typeSection[0].questionType.questionTypeID,
        typeSection: categoryGroup,
      };
      result.push(group);
      return result;
    },
    []
  );

const groupQuestionsByCategory = x =>
  reduce(
    groupBy(x, 'displayCategory.categoryName'),
    (result, categorySection, key) => {
      const group = { key, categorySection };
      result.push(group);
      return result;
    },
    []
  );

class Questionnaire extends Component {
  state = {
    acceptPrescreen: this.props.request.prescreenAccepted,
    attachments: [],
    noDocumentsError: false,
    grouped: [],
  };

  componentDidMount() {
    const { id } = this.props.match.params;
    //Force questionnaire instace to load first.
    //This prevents a Race condition
    this.props
      .fetchQuestionnaireInstance({
        vendorRequestID: id,
        status: hasPermission(
          this.props.appUser.permissions,
          permissions.MODIFY_ANY_QUESTIONNAIRE
        )
          ? 'ALL'
          : 'ACTIVE',
      })
      .then(() => {
        this.props.fetchPrescreenInstance(id);
        this.props.fetchVendorRequest(id);
        this.props.fetchUploadedFiles(id);
        this.props.questions.result.forEach(
          (question, i) => (question['questionNumber'] = i)
        );
        const grouped = groupQuestionsByType(this.props.questions.result);
        this.setState({ grouped });
      });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.questions.result.length !== this.props.questions.result.length
    ) {
      const grouped = groupQuestionsByType(this.props.questions.result);
      this.setState({ grouped });
    }
    if (
      prevProps.questionnaireFiles.length !==
      this.props.questionnaireFiles.length
    ) {
      this.setState({ attachments: this.props.questionnaireFiles });
    }
    if (
      this.props.request.vendorRequestID !== prevProps.request.vendorRequestID
    ) {
      this.setState({ acceptPrescreen: this.props.request.prescreenAccepted });
    }
    if (
      this.props.request.vendorRequestID ===
        prevProps.request.vendorRequestID &&
      this.props.request.prescreenAccepted !==
        prevProps.request.prescreenAccepted
    ) {
      this.setState({ acceptPrescreen: this.props.request.prescreenAccepted });
    }
  }

  handleReject = () => {
    // Redirect to dashboard
    this.props.history.push('/');
  };

  handleAccept = () => {
    const { id } = this.props.match.params;
    acceptPrescreenAnswers(id)
      .then(res => {
		// refresh page, this loaded with the 'original answers', and doesn't refresh, so we need to reload to make sure we picked up any changes while accepting the prescreen
		this.props.history.go(0);
	  })
      .catch(err => console.log(err));
  };

  handleOnDrop = acceptedFiles => {
    const { vendorRequestID } = this.props.request;
    acceptedFiles = acceptedFiles.filter(file => fileTypeFilter(file));
    if (acceptedFiles.length === 0) {
      AppToaster.show({
        message:
          'The files could not be uploaded due to their file type. Try uploading a PDF, MSG, Excel / Word / PowerPoint, or html file',
        intent: Intent.DANGER,
      });
      return;
    }
    forEach(acceptedFiles, file => {
      uploadFile('QUESTIONNAIRE', this.props.match.params.id, file)
        .then(res => {
          AppToaster.show({
            message: res.data.message,
            intent: Intent.SUCCESS,
          });
          this.setErrorIfNoDocuments(false);
          this.props.fetchUploadedFiles(vendorRequestID);
        })
        .catch(err => {
          AppToaster.show({
            message: err.response.data.message,
            intent: Intent.DANGER,
          });
        });
    });
  };

  handleSave = values => {
    saveQuestionnaire('SAVE', this.props.match.params.id, values)
      .then(res => {
        AppToaster.show({
          message: res.data.message,
          intent: Intent.SUCCESS,
        });
        // Redirect to homepage
        this.props.history.push('/');
      })
      .catch(error => {
        AppToaster.show({
          message: error.response.data.message,
          intent: Intent.DANGER,
        });
      });
  };

  handleSubmit = (values, actions) => {
    saveQuestionnaire('SUBMIT', this.props.match.params.id, values)
      .then(res => {
        actions.setSubmitting(false);
        AppToaster.show({
          message: res.data.message,
          intent: Intent.SUCCESS,
        });
        this.props.history.push('/');
      })
      .catch(error => {
        actions.setSubmitting(false);
        AppToaster.show({
          message: error.response.data.message,
          intent: Intent.DANGER,
        });
      });
  };

  setErrorIfNoDocuments = noDocumentsError => {
    this.setState({ noDocumentsError });
  };

  validateForm = values => {
    let errors = {};
    let uploadErrors = {};
    forEach(values, (val, i) => {
      if (
        val.selected.answer === 'OPT_OUT' &&
        this.state.attachments.length === 0
      ) {
        errors.attachments =
          'If you opt out of a section you must upload an attachment.';

        this.setErrorIfNoDocuments(true);
        uploadErrors[i] = '';
      }
      if (val.selected.length > 0 || !isNaN(val.selected.answerID)) {
        return;
      } else {
        errors[i] =
          'This is a required field. Please answer this question to continue.';
      }
    });
    //Check if there were any errors
    if (!errors.attachments && Object.keys(errors).length > 0) {
      //Snap to the question that has an error
      window.location = `#${values[Object.keys(errors)[0]].questionID}`;
      //This is done because there is a static top nav bar that sits on top of the container when we snap to an element.
      //set the margin to 0
      document.getElementById('contentContainer').style.marginTop = '0';
      //reload the page so that the margin disappears
      this.setState(this.state, () => {
        //reset the margin to 98px (so the container appears below the top nav bar)
        document.getElementById('contentContainer').style.marginTop = '98px';
      });
    } else if (errors.attachments) {
      const sectionID =
        values[Object.keys(uploadErrors)[0]].questionType.questionTypeID;

      window.location = `#section-${sectionID}`;
      //Fix the styling on snap
      document.getElementById('contentContainer').style.marginTop = '0';
      //reload the page so that the margin disappears
      this.setState(this.state, () => {
        //reset the margin to 98px (so the container appears below the top nav bar)
        document.getElementById('contentContainer').style.marginTop = '98px';
      });
    }
    return errors;
  };

  render() {
    const { acceptPrescreen, noDocumentsError } = this.state;
    const { request, fetchUploadedFiles } = this.props;
    if (this.props.isFetching || this.props.isRequestFetching)
      return <Loading size={200} />;
    if (this.props.isError) {
      return (
        <div
          style={{
            width: 'max-content',
            background: '#fff',
            padding: '3rem',
            margin: '0 auto',
          }}
        >
          <NonIdealState
            icon="error"
            title="No Active Questionnaire Found"
            description="This questionnaire may have already been submitted or disputed."
          />
        </div>
      );
    }
    if (!acceptPrescreen)
      return (
        <ReviewPrescreen
          handleAccept={this.handleAccept}
          handleReject={this.handleReject}
          instance={this.props.prescreenInstance}
          isError={this.props.preScreenIsError}
          isFetching={this.props.preScreenIsFetching}
          id={this.props.match.params.id}
          request={this.props.request}
          history={this.props.history}
        />
      );
	  
    return (
      <Container>
        {acceptPrescreen && (
          <React.Fragment>
            <DrawerHeader>
              Questionnaire -{' '}
              {request.vendorInformation
                ? request.vendorInformation.vendorName
                : ' '}{' '}
              | {request.productServiceName}
            </DrawerHeader>
            <PaddingWrapper style={{ margin: '0 auto', width: '95%' }}>
              <div style={{ display: 'flex' }}>
                <AddAttachmentsSection handleOnDrop={this.handleOnDrop} />
                <UploadedAttachments
                  attachments={this.props.questionnaireFiles}
                  fetchUploadedFiles={fetchUploadedFiles}
                  request={request}
                />
              </div>
              <Formik
                onSubmit={this.handleSubmit}
                initialValues={keyBy(this.props.questions.result, 'questionID')}
                validate={this.validateForm}
                validateOnChange={false}
                validateOnBlur={false}
              >
                {({
                  setFieldValue,
                  handleChange,
                  values,
                  isSubmitting,
                  isValidating,
                  errors,
                  submitCount,
                }) => (
                  <Form>
                    {errors.attachments &&
                    isSubmitting &&
                    isValidating &&
                    noDocumentsError
                      ? AppToaster.show({
                          message: errors.attachments,
                          intent: Intent.DANGER,
                        })
                      : null}
                    {map(this.state.grouped, (questionGroup, i) => (
                      <QuestionTypeSection
                        key={`${i}-${this.props.match.params.id}`}
                        questionGroup={questionGroup}
                        setFieldValue={setFieldValue}
                        handleChange={handleChange}
                        values={values}
                        errors={errors}
                        requestID={this.props.match.params.id}
                        handleOnDrop={this.handleOnDrop}
                        appUser={this.props.appUser}
                        submitCount={submitCount}
                        attachments={this.state.attachments}
                      />
                    ))}
                    <div
                      style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                      <PlainButton
                        type="button"
                        onClick={() => this.handleSave(values)}
                        disabled={isSubmitting}
                      >
                        SAVE & FINISH LATER
                      </PlainButton>
                      {/* adds a check to prevent outside Third Parties from submitting */}
                      {this.props.questions &&
                      hasPermission(
                        this.props.appUser.permissions,
                        permissions.MODIFY_ANY_QUESTIONNAIRE
                      ) ? (
                        <PlainButton type="submit" disabled={isSubmitting}>
                          Submit
                        </PlainButton>
                      ) : this.props.questions &&
                        this.props.questions.vendorContactID ===
                          this.props.appUser.userID ? (
                        <PlainButton type="submit" disabled={isSubmitting}>
                          Submit
                        </PlainButton>
                      ) : this.props.questions &&
					  this.props.appUser.thirdPartyIDs.includes(`${this.props.questions.vendorID}`) ? (
						<PlainButton type="submit" disabled={isSubmitting}>
							Submit
						</PlainButton>
					  ) : (
                        <Tooltip
                          position={Position.BOTTOM}
                          content={
                            <>
                              You can save your changes, but only
                              <br />
                              an associated Third Party Member
                              <br />
                              is able to submit the questionnaire.
                            </>
                          }
                        >
                          <PlainButton type="submit" disabled={true}>
                            Submit
                          </PlainButton>
                        </Tooltip>
                      )}
                    </div>
                  </Form>
                )}
              </Formik>
              <div style={{ display: 'flex' }}>
                <AddAttachmentsSection handleOnDrop={this.handleOnDrop} />
                <UploadedAttachments
                  attachments={this.props.questionnaireFiles}
                  fetchUploadedFiles={fetchUploadedFiles}
                  request={request}
                />
              </div>
            </PaddingWrapper>
          </React.Fragment>
        )}
      </Container>
    );
  }
}

const mapStateToProps = state => ({
  questions: state.questionnaire.instance,
  isFetching: state.questionnaire.instanceIsFetching,
  isError: state.questionnaire.instanceErrorMessage,
  prescreenInstance: state.prescreen.instance,
  preScreenIsFetching: state.prescreen.instanceIsFetching,
  preScreenIsError: state.prescreen.instanceErrorMessage,
  request: state.vendorRequest.singleRequest,
  isRequestFetching: state.vendorRequest.isFetching,
  questionnaireFiles: state.utils.questionnaireFiles,
});

Questionnaire = connect(
  mapStateToProps,
  {
    fetchPrescreenInstance,
    fetchQuestionnaireInstance,
    fetchVendorRequest,
    fetchUploadedFiles,
  }
)(Questionnaire);

export default Questionnaire;
